import type { ReactElement } from "react"
import { createElement } from "react"

import { QueryClient } from "@tanstack/react-query"
import compact from "lodash-es/compact"
import concat from "lodash-es/concat"
import eq from "lodash-es/eq"
import find from "lodash-es/find"
import first from "lodash-es/first"
import isEmpty from "lodash-es/isEmpty"
import map from "lodash-es/map"
import { DateTime } from "luxon"

import { coachingSessionParticipantsAdapter } from "@/3514/adapters"
import {
    ECoachNavigationStepperBulletCompletion as EStepperBulletCompletion,
    ECoachNavigationStepperBulletOrder as EStepperBulletOrder,
    ECoachNavigationStepperBulletState as EStepperBulletState,
    ETypographySize,
    type TModuleExcerptModuleUiModel,
    type TModuleExcerptPhase,
    type TCoachNavigationStepperBullet as TStepperBullet,
    Typography
} from "@/3514/components"
import { ECoachingSessionsGroupQueryKey } from "@/3514/config"
import { EPath } from "@/3514/constants"
import {
    EModulePhaseProgressStatus,
    EModulePhaseTrackStatus,
    type ICoachingSessionDto,
    type ICoachingSessionParticipantDto,
    type ICoachingSessionsQueryDto,
    type IModuleExcerptAssociatedSessionModel,
    type ISessionParticipantModel
} from "@/3514/types"
import { renderRichContent } from "@/3514/utils"
import { richTextRendererUtils } from "@/shared/rich-text-renderer"

import type {
    ICoachGroupSessionParticipantModulePhaseModel,
    ICoachGroupSessionParticipantProgressDto as IIndividualProgressDto
} from "./coach.group.api.types"
import mocks from "./coach.group.mocks"
import type { TCoachGroupSessionNavigationConfig as TNavConfig } from "./coach.group.ui.types"

const { isStringPlainText } = richTextRendererUtils

function mapSessionNavigationListToStepperBulletList({
    navs,
    groupId,
    sessionId
}: {
    navs: TNavConfig[]
    groupId: number
    sessionId: number
}): TStepperBullet[] {
    return map<TNavConfig, TStepperBullet>(
        navs,
        (n: TNavConfig, idx: number): TStepperBullet => ({
            href: EPath.Coach_Group({ groupId }, { sessionId: n.id, tab: "Summary" }),
            pageId: n.id,
            order: eq(sessionId, n.id) ? EStepperBulletOrder.Current : EStepperBulletOrder.NotCurrent,
            completion:
                !n.isUpcoming || n.isLive ? EStepperBulletCompletion.Completed : EStepperBulletCompletion.NotCompleted,
            state: EStepperBulletState.Normal,
            title: `Session ${idx + 1}`,
            subTitle: !!n.date ? DateTime.fromISO(n.date).toFormat("MMM d") : String("")
        })
    )
}

function getModuleExcerptUiModel({
    rank,
    session,
    phases,
    arePhasesHidden
}: {
    rank: number
    session: IModuleExcerptAssociatedSessionModel
    phases: ICoachGroupSessionParticipantModulePhaseModel[]
    arePhasesHidden: boolean
}): TModuleExcerptModuleUiModel {
    return {
        rank,
        session,
        id: Number(0),
        isStub: isEmpty(phases),
        phases: isEmpty(phases)
            ? arePhasesHidden
                ? []
                : Array.from(
                      { length: 4 },
                      (_: unknown, idx: number): TModuleExcerptPhase => ({
                          order: idx + 1,
                          progressStatus: EModulePhaseProgressStatus.NotAvailable,
                          trackStatus: EModulePhaseTrackStatus.NotStarted
                      })
                  )
            : map<ICoachGroupSessionParticipantModulePhaseModel, TModuleExcerptPhase>(
                  phases,
                  (
                      { trackStatus, progressStatus }: ICoachGroupSessionParticipantModulePhaseModel,
                      index: number
                  ): TModuleExcerptPhase => ({
                      trackStatus,
                      progressStatus,
                      order: index
                  })
              )
    }
}

function renderSummaryDetail({
    id,
    richClasses,
    content,
    isTextSmall = false
}: {
    id: string
    richClasses: string
    content: string
    isTextSmall?: boolean
}): ReactElement {
    return isStringPlainText(content)
        ? createElement(Typography, {
              text: content,
              size: isTextSmall ? ETypographySize.Small : ETypographySize.Medium,
              className: "inline"
          })
        : renderRichContent({
              identifier: id,
              classes: richClasses,
              allowedTags: ["ul", "li", "p", "span"]
          })(content)
}

function getIndividualPlaceholderData(params: {
    firstName: string
    photo: string | null
    coachPhoto: string | null
}): IIndividualProgressDto {
    return mocks.dtos.getIndividualDto(params)
}

function getIndividualData({
    qc,
    individualId,
    groupId,
    sessionId
}: {
    qc: QueryClient
    individualId: number
    groupId: number
    sessionId: number
}): ISessionParticipantModel {
    const rawGroupData: ICoachingSessionsQueryDto = qc.getQueryData(ECoachingSessionsGroupQueryKey({ groupId }))

    const currentSession: ICoachingSessionDto = find<ICoachingSessionDto>(
        compact<ICoachingSessionDto>(
            concat<ICoachingSessionDto>(
                rawGroupData.upcoming_sessions,
                rawGroupData.passed_sessions,
                rawGroupData.live_session
            )
        ),
        { session_id: sessionId }
    )

    return first<ISessionParticipantModel>(
        coachingSessionParticipantsAdapter([
            find<ICoachingSessionParticipantDto>(currentSession.participants, {
                id: individualId
            })
        ])
    )
}

export default {
    mapSessionNavigationListToStepperBulletList,
    getModuleExcerptUiModel,
    getIndividualPlaceholderData,
    renderSummaryDetail,
    getIndividualData
}
